import { Check, CloudSun, Loader2, RefreshCw, WifiOff } from 'lucide-react'

import { useRoadbook } from '../../data/roadbookContext'
import { timeAgo } from '../../lib/format'
import { weatherProposal, weatherState, WEATHER_ATTRIBUTION } from '../../lib/weather'
import type { DiaryEntry, Stage } from '../../lib/types'
import { useOnline } from './weatherApi'

/**
 * Der Wettervorschlag aus der Aufzeichnung.
 *
 * Dieselbe Machart wie die Streckenberechnung und der Track-Block daneben: die
 * Werte liegen im Tagebuch, der Satz daraus steht sichtbar da, und übernommen
 * wird er nur auf Knopfdruck. Ein Vorschlag überschreibt niemals, was jemand
 * eingetragen hat, und bleibt als Vorschlag erkennbar, bis er angenommen wurde.
 *
 * Ohne Aufzeichnung steht hier gar nichts - kein leerer Kasten, keine
 * Aufforderung. Das ist die Regel dieser Funktion und keine Sparsamkeit der
 * Oberfläche: aus Zielkoordinate und geratener Ankunftszeit käme eine plausible
 * Zahl heraus, die aussieht wie eine Messung, und die wäre schlimmer als gar
 * keine. Wer nicht aufgezeichnet hat, schreibt das Wetter selbst hin; er war ja
 * dabei.
 */
export function WeatherPanel({
  stage,
  entry,
  laeuft,
  onHolen,
  nurLesen = false,
}: {
  stage: Stage
  entry: DiaryEntry
  /** Läuft gerade ein Durchgang des Nachlaufs? */
  laeuft: boolean
  /** Holt genau diese Etappe, auf Knopfdruck. */
  onHolen: (stage: Stage, entry: DiaryEntry) => void
  /** Ohne Schreibrecht bleibt nur der Satz stehen, siehe unten. */
  nurLesen?: boolean
}) {
  const { setDiary } = useRoadbook()
  const online = useOnline()

  const zustand = weatherState(entry)
  // Ohne Aufzeichnung gibt es hier nichts zu sagen. Ein Block mit dem Hinweis,
  // dass ohne GPX kein Wetter kommt, stünde auf jeder zweiten Etappe und wäre
  // eine Belehrung statt einer Auskunft.
  if (zustand === 'no-track') return null

  const wetter = entry.weatherFromTrack
  const vorschlag = weatherProposal(entry)

  // Nur lesende Rolle: der Satz bleibt sichtbar, alles Übrige an diesem Block
  // ist Holen und Übernehmen - Handgriffe, die diese Rolle nicht hat.
  if (nurLesen) {
    if (!vorschlag) return null
    return (
      <section className="wetter-block">
        <h5 className="block-titel">
          <CloudSun size={13} /> Wetter
        </h5>
        <p className="wetter-satz">{vorschlag.text}</p>
        <p className="abschluss">{wetter?.attribution || WEATHER_ATTRIBUTION}</p>
      </section>
    )
  }

  return (
    <section className="wetter-block">
      <h5 className="block-titel">
        <CloudSun size={13} /> Wetter
      </h5>

      {(zustand === 'none' || zustand === 'stale') &&
        (online ? (
          <p className="leer kein-druck">
            {laeuft ? (
              <>
                <Loader2 size={13} className="dreht" /> Wird geholt …
              </>
            ) : zustand === 'stale' ? (
              'Die Aufzeichnung hat sich geändert, seit das Wetter geholt wurde. Für den neuen Zeitraum steht es noch aus.'
            ) : (
              'Aus dieser Aufzeichnung lässt sich das Wetter des Tages holen.'
            )}
          </p>
        ) : (
          /*
            Kein Fehler, ein Wartezustand. Der Import passiert womöglich im
            Funkloch, und dass hier etwas abrufbar ist, sagt der Track selbst -
            es geht nichts verloren, wenn gerade niemand Empfang hat.
          */
          <p className="leer kein-druck">
            <WifiOff size={13} /> Kein Netz. Für diesen Tag ist Wetter abrufbar — es wird geholt,
            sobald wieder eine Verbindung da ist.
          </p>
        ))}

      {zustand === 'current' && !vorschlag && (
        <p className="leer kein-druck">
          Für diesen Tag hat der Wetterdienst keine Werte. Das Feld oben bleibt leer, bis jemand
          etwas hineinschreibt.
        </p>
      )}

      {/*
        Zwei verschiedene Sätze, und der Unterschied ist der ganze Punkt: „gibt
        es nicht“ steht oben und gilt für einen Tag, den der Dienst fertig hat.
        Hier steht „noch nicht“ - gefragt wurde am Abend der Fahrt selbst, und da
        reicht das Archiv des Dienstes noch nicht bis heute. Es wird von selbst
        nachgeholt, dafür ist nichts zu drücken.
      */}
      {zustand === 'pending' && (
        <p className="leer kein-druck">
          Der Wetterdienst hat für diesen Tag noch keine Werte — sein Archiv hinkt der Gegenwart ein
          paar Tage nach. Es wird in den nächsten Tagen von selbst noch einmal geholt.
        </p>
      )}

      {vorschlag && (
        <>
          {/*
            Der Satz steht als Vorschlag da und nicht als Angabe: die Marke
            davor sagt, was er ist, und die Zeile darunter, was gerade im
            Tagebuch steht. Ohne diesen Vergleich wäre der Knopf eine
            Aufforderung, etwas zu ersetzen, das man nicht sieht - genau wie bei
            der Strecke und beim Track.
          */}
          <p className="wetter-satz">
            {!vorschlag.accepted && <span className="wetter-marke">Vorschlag</span>}{' '}
            {vorschlag.text}
          </p>
          <dl className="strecke-werte">
            <div>
              <dt>Im Tagebuch jetzt</dt>
              <dd>{vorschlag.current || 'nichts eingetragen'}</dd>
            </div>
            <div>
              <dt>Aus der Aufzeichnung</dt>
              <dd>
                {String(wetter?.hours.length ?? 0)} Stunden entlang der Fahrt
                {wetter?.fetchedAt ? `, geholt ${timeAgo(wetter.fetchedAt)}` : ''}
              </dd>
            </div>
          </dl>

          <div className="knopfreihe kein-druck">
            {/*
              Der einzige Weg vom Vorschlag ins Tagebuch führt über diesen
              Knopf. Steht schon etwas im Feld, sagt die Beschriftung, dass es
              ersetzt wird - ungefragt überschreibt hier nichts.
            */}
            <button
              type="button"
              className="knopf"
              disabled={vorschlag.accepted}
              onClick={() => setDiary(stage.id, { weather: vorschlag.text })}
            >
              <Check size={14} />{' '}
              {vorschlag.accepted
                ? 'Übernommen'
                : vorschlag.current
                  ? 'Vorschlag übernehmen und ersetzen'
                  : 'Vorschlag übernehmen'}
            </button>
          </div>
        </>
      )}

      {/*
        Der Knopf steht auch dann da, wenn der Nachlauf zuständig wäre: der
        kann aufgegeben haben - abgelaufene Anmeldung, ein Dienst, der nicht
        antwortet -, und dann soll niemand auf ein Ereignis warten müssen, das
        nicht mehr kommt. Ohne Netz ist er aus; dort wartet der Nachlauf.
      */}
      {online && (
        <div className="knopfreihe kein-druck">
          <button
            type="button"
            className="knopf still"
            disabled={laeuft}
            onClick={() => {
              onHolen(stage, entry)
            }}
          >
            {laeuft ? <Loader2 size={14} className="dreht" /> : <RefreshCw size={14} />}
            {zustand === 'none' || zustand === 'stale' ? 'Wetter holen' : 'Noch einmal holen'}
          </button>
        </div>
      )}

      {/*
        Die Namensnennung steht hier und nicht in einer versteckten Ecke: die
        Daten stehen unter CC BY 4.0, und die verlangt sie dort, wo die Werte
        erscheinen. Genauso wie „© OpenStreetMap contributors“ fest in der Karte
        steht (features/map/basemap.ts).
      */}
      <p className="abschluss">
        {wetter?.attribution || WEATHER_ATTRIBUTION}. Die Werte sind Stundenwerte an den Orten, an
        denen die Aufzeichnung war — kein Messwert vom Fahrzeug, sondern die Auskunft eines
        Wetterarchivs zu diesem Ort und dieser Stunde.
      </p>
    </section>
  )
}
